import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Check } from "lucide-react";

const base = { name: "", organisation: "", email: "", phone: "", location: "", interest: "", audience: "individual", current_activity: "", goals: "", timeframe: "" };
const seedBankExtra = { seed_bank_supports: "", seed_bank_users: "", facilities_exist: "no", priority_species: "" };

function formatError(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function EnquiryForm({ formType = "contact", theme = "light", title = "Make an enquiry", cta = "Send enquiry" }) {
  const isSeedBank = formType === "seed-bank";
  const [form, setForm] = useState({ ...base, ...(isSeedBank ? seedBankExtra : {}) });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Please add your name and email."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) { toast.error("Please enter a valid email address."); return; }
    setSubmitting(true);
    try {
      await api.post("/enquiries", { ...form, form_type: formType });
      setDone(true);
      toast.success("Thank you — we'll be in touch soon.");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  const dark = theme === "dark";
  const labelCls = `overline ${dark ? "text-cream/50" : "text-charcoal/45"} mb-2 block`;
  const inputCls = dark
    ? "w-full bg-transparent border border-cream/25 px-4 py-3 text-cream placeholder:text-cream/40 focus:outline-none focus:border-ochre transition-colors"
    : "w-full bg-transparent border border-charcoal/20 px-4 py-3 text-charcoal placeholder:text-charcoal/40 focus:outline-none focus:border-ochre transition-colors";
  const border = dark ? "border-cream/25" : "border-charcoal/12";

  if (done) {
    return (
      <div className={`border ${border} ${dark ? "bg-charcoal/40 text-cream" : "bg-cream"} p-10 md:p-12 text-center`} data-testid="enquiry-success">
        <div className="w-14 h-14 rounded-full bg-ochre mx-auto flex items-center justify-center mb-6"><Check className="text-cream" /></div>
        <h3 className="font-serif text-3xl mb-3">Thank you.</h3>
        <p className={dark ? "text-cream/75 font-light" : "text-charcoal/70 font-light"}>
          Your enquiry has reached SeedKeepers. We'll be in touch soon to discuss the right place to begin.
        </p>
      </div>
    );
  }

  const F = ({ label, children }) => (
    <label className="block"><span className={labelCls}>{label}</span>{children}</label>
  );

  return (
    <form onSubmit={submit} className={`border ${border} ${dark ? "bg-charcoal/40 backdrop-blur-xl" : "bg-cream"} p-8 md:p-10 space-y-5`} data-testid="enquiry-form">
      {title && <h3 className={`font-serif text-2xl ${dark ? "text-cream" : "text-rainforest"} mb-2`}>{title}</h3>}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <F label="Full name *"><input value={form.name} onChange={set("name")} data-testid="enq-name" required className={inputCls} placeholder="Your name" /></F>
        <F label="Organisation"><input value={form.organisation} onChange={set("organisation")} data-testid="enq-org" className={inputCls} placeholder="Optional" /></F>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <F label="Email *"><input type="email" value={form.email} onChange={set("email")} data-testid="enq-email" required className={inputCls} placeholder="you@email.com" /></F>
        <F label="Phone"><input value={form.phone} onChange={set("phone")} data-testid="enq-phone" className={inputCls} placeholder="Optional" /></F>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <F label="Location / region"><input value={form.location} onChange={set("location")} data-testid="enq-location" className={inputCls} placeholder="e.g. Pilbara, WA" /></F>
        <F label="This enquiry is for">
          <select value={form.audience} onChange={set("audience")} data-testid="enq-audience" className={inputCls}>
            <option value="individual">An individual</option>
            <option value="team">A team / organisation</option>
          </select>
        </F>
      </div>
      <F label="Area of interest"><input value={form.interest} onChange={set("interest")} data-testid="enq-interest" className={inputCls} placeholder="e.g. Seed collection, seed bank, on-country training" /></F>
      <F label="Current level of native seed activity"><input value={form.current_activity} onChange={set("current_activity")} data-testid="enq-activity" className={inputCls} placeholder="e.g. Just starting / already collecting" /></F>
      <F label="What you hope to establish or improve"><textarea value={form.goals} onChange={set("goals")} data-testid="enq-goals" rows={3} className={`${inputCls} resize-none`} placeholder="Tell us a little about your goals…" /></F>
      <F label="Preferred timeframe"><input value={form.timeframe} onChange={set("timeframe")} data-testid="enq-timeframe" className={inputCls} placeholder="e.g. This season / next 6 months" /></F>

      {isSeedBank && (
        <div className={`pt-2 border-t ${border} space-y-5`}>
          <F label="What the seed bank needs to support"><textarea value={form.seed_bank_supports} onChange={set("seed_bank_supports")} rows={2} data-testid="enq-sb-supports" className={`${inputCls} resize-none`} placeholder="Restoration, fire recovery, enterprise…" /></F>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <F label="Likely users of the collection"><input value={form.seed_bank_users} onChange={set("seed_bank_users")} data-testid="enq-sb-users" className={inputCls} placeholder="Rangers, nursery, community…" /></F>
            <F label="Do facilities already exist?">
              <select value={form.facilities_exist} onChange={set("facilities_exist")} data-testid="enq-sb-facilities" className={inputCls}>
                <option value="no">No</option><option value="partial">Partially</option><option value="yes">Yes</option>
              </select>
            </F>
          </div>
          <F label="Known priority species or ecosystems"><input value={form.priority_species} onChange={set("priority_species")} data-testid="enq-sb-species" className={inputCls} placeholder="Optional" /></F>
        </div>
      )}

      <button type="submit" disabled={submitting} data-testid="enquiry-submit" className="w-full bg-ochre text-cream py-4 text-sm tracking-wide hover:bg-copper transition-colors disabled:opacity-60">
        {submitting ? "Sending…" : cta}
      </button>
    </form>
  );
}
